import { ElementRef, Inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment, WEBRTC_CONFIG_DEFAULT } from '@environment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { delay, exhaustMap, filter, skip, take } from 'rxjs/operators';
import { ConfigService } from '@mona/config';
import { DeviceFacade } from '@mona/device/data-access-device';
import { CallConfig, CallSession, CallWsEvent, CallWsMessage, WebRtcMediaConfig } from '@mona/models';
import { MonaRpcService } from '@mona/rpc';
import { Logger } from '@mona/shared/logger';
import { get, MEDIA_ERRORS } from '@mona/shared/utils';
import {
    MediaDataService,
    PipCallDialogComponent,
    PipCallDialogComponentCloseReason,
    PipDialogService,
} from '@mona/telemedicine/shared';
import { DialogService, MessageService } from '@mona/ui';
import { CallWsService } from '../infrastructure';
import { CallService } from './call.service';

export const TELEMEDICINE_DIALOG_ID = 'telemedicine-pip';

/**
 * Call control service
 */
@Injectable({
    providedIn: 'root',
})
export class CallWebrtcService implements MediaDataService {
    /**
     * Is the camera on
     */
    cameraActive$ = new BehaviorSubject<boolean>(true);

    /**
     * Is the mic on
     */
    microphoneActive$ = new BehaviorSubject<boolean>(true);

    /**
     * Is call partner joined
     */
    hasCallPartner$ = new BehaviorSubject<boolean>(false);

    /**
     * Has call partner video disabled
     */
    hasCallPartnerVideoDisabled$ = new BehaviorSubject<boolean>(false);

    /**
     * Has call partner audio disabled (muted)
     */
    hasCallPartnerAudioDisabled$ = new BehaviorSubject<boolean>(false);

    /**
     * If the call is running in PIP mode
     */
    isPip$ = new BehaviorSubject<boolean>(false);

    /**
     * If the call is using screen capture
     */
    isScreenCapture$ = new BehaviorSubject(false);

    /**
     * Reference to the pip video dialog
     */
    pipDialog: MatDialogRef<PipCallDialogComponent>;

    /**
     * True while PIP hangup teardown runs (afterClosed must not treat it as fullscreen).
     */
    private hangUpFromPipInProgress = false;

    /**
     * Local audio sender reference for the call
     */
    audioSender: RTCRtpSender;

    /**
     * Local video sender reference for the call
     */
    videoSender: RTCRtpSender;

    private _stream: MediaStream;

    /**
     * Stream
     */
    get stream(): MediaStream {
        return this._stream;
    }
    set stream(value: MediaStream) {
        this._stream = value;
    }

    /**
     * Screen Capture Stream
     */
    private screenCaptureStream: MediaStream;

    /**
     * Remote stream
     */
    private remoteStream: MediaStream;

    /**
     * Holds a reference to the local video element
     */
    private localVideoElement: ElementRef<HTMLVideoElement>;

    /**
     * Holds a reference to the remote video element
     */
    private remoteVideo: ElementRef<HTMLVideoElement>;

    /**
     * Call config
     */
    private callConfig: CallConfig;

    /**
     * Rtc peer connection
     */
    private rtcPeerConnection: RTCPeerConnection;

    /**
     * Service subscriptions holder
     */
    private sessionSubscriptionsHolder: Subscription = new Subscription();

    /**
     * PIP subscriptions holder
     */
    private pipSubscriptionsHolder: Subscription = new Subscription();

    private readonly logger = new Logger('TELEMEDICINE');

    /**
     * Constructor
     * @param rpcService
     * @param callWsService CallWsService
     * @param callService CallService
     * @param messageService MessageService
     * @param deviceFacade DeviceFacade
     * @param dialogService DialogService
     * @param pipDialogService PipDialogService
     * @param configService
     * @param router
     */
    constructor(
        private rpcService: MonaRpcService,
        private callWsService: CallWsService,
        private callService: CallService,
        private messageService: MessageService,
        private deviceFacade: DeviceFacade,
        private dialogService: DialogService,
        private pipDialogService: PipDialogService,
        private configService: ConfigService,
        private router: Router,
    ) {}

    /**
     * Is picture in picture active
     */
    getIsPip$() {
        return this.isPip$;
    }

    /**
     * Has Calling partner
     */
    getHasCallPartner$() {
        return this.hasCallPartner$;
    }

    /**
     * Get setting from configured WebRtc Media Config
     * @param path
     */
    getWebRtcMediaConfig<K extends Path<WebRtcMediaConfig>>(path: K): PathValue<WebRtcMediaConfig, K> {
        return get(WEBRTC_CONFIG_DEFAULT as WebRtcMediaConfig, path);
    }

    /**
     * Inits call
     * @param localVideo ElementRef<HTMLVideoElement>
     * @param remoteVideo ElementRef<HTMLVideoElement>
     * @param callConfig CallConfig
     */
    async initCall(
        localVideo: ElementRef<HTMLVideoElement>,
        remoteVideo: ElementRef<HTMLVideoElement>,
        callConfig: CallConfig,
    ): Promise<void> {
        this.deviceFacade.invokePreCallScript();

        this.localVideoElement = localVideo;
        this.remoteVideo = remoteVideo;
        this.callConfig = callConfig;

        this.deviceFacade.isCameraActivated$.pipe(filter(Boolean), take(1)).subscribe(async () => {
            this.onCameraLoaded();
        });
    }

    private async onCameraLoaded(): Promise<void> {
        await this.createStream();

        navigator.mediaDevices.ondevicechange = async () => {
            await this.createStream();
        };

        this.sessionSubscriptionsHolder.add(
            this.callWsService.wsError$.subscribe(err => {
                this.messageService.errorToast('diagnostics.telemedicineWSNotAvailable', err);
                this.router.navigate(['/telemedicine']);
            }),
        );

        this.sessionSubscriptionsHolder.add(
            this.callWsService.connection$.subscribe(connection => {
                if (connection) {
                    this.messageService.successToast('diagnostics.telemedicineWSAvailable');
                }
            }),
        );

        this.sessionSubscriptionsHolder.add(
            this.callWsService.wsMessages$.subscribe(message => {
                this.handleWsEvents(message);
            }),
        );

        await this.callService
            .getCallSession()
            .pipe(
                filter<CallSession>(Boolean),
                take(1),
                exhaustMap(session => this.callWsService.connect(session.id)),
            )
            .toPromise();
    }

    /**
     * Inits call
     * @param localVideo ElementRef<HTMLVideoElement>
     * @param remoteVideo ElementRef<HTMLVideoElement>
     * @param callConfig CallConfig
     */
    async restoreActiveCall(
        localVideo: ElementRef<HTMLVideoElement>,
        remoteVideo: ElementRef<HTMLVideoElement>,
        callConfig: CallConfig,
    ): Promise<void> {
        this.localVideoElement = localVideo;
        this.remoteVideo = remoteVideo;
        this.callConfig = callConfig;

        this.localVideoElement.nativeElement.srcObject = this.stream;
        this.remoteVideo.nativeElement.srcObject = this.remoteStream;
    }

    /**
     * Enables Pip. Resolves when the dialog is open and streams are bound
     * (so screen capture of the Mona window can include the face tiles — MD-11582).
     */
    enablePip(): Promise<void> {
        // Already open — streams should already be bound
        if (this.pipDialog) {
            return Promise.resolve();
        }

        // Track pip mode
        this.isPip$.next(true);

        // Open dialog
        let session: CallSession;
        this.callService
            .getCallSession()
            .pipe(take(1))
            .subscribe(value => (session = value));

        this.dialogService.open<PipCallDialogComponent, PipCallDialogComponentCloseReason>(
            PipCallDialogComponent,
            {
                videoEnabled: this.cameraActive$.getValue(),
                microphoneEnabled: this.microphoneActive$.getValue(),
                remoteVideoDisabled: this.hasCallPartnerVideoDisabled$.getValue(),
                remoteAudioDisabled: this.hasCallPartnerAudioDisabled$.getValue(),
                remoteParticipantName: session?.externalParticipantName || '',
                localParticipantName: session?.internalParticipantName || '',
                hasCallPartner: this.hasCallPartner$.getValue(),
                isScreenCapture: this.isScreenCapture$.getValue(),
            },
            {
                id: TELEMEDICINE_DIALOG_ID,
                minWidth: undefined,
                minHeight: undefined,
                panelClass: 'pip-call-dialog__panel',
                hasBackdrop: false,
                closeOnNavigation: false,
                position: {
                    bottom: '42px',
                    left: '142px',
                },
            },
        );

        this.pipDialog = this.dialogService.getDialogById(TELEMEDICINE_DIALOG_ID);

        // Dialog close listener
        this.pipDialog.afterClosed().subscribe((reason: PipCallDialogComponentCloseReason) => {
            this.pipSubscriptionsHolder.unsubscribe();
            this.pipSubscriptionsHolder = new Subscription();

            // Hangup already handled via requestHangUp (before close). Do not treat as fullscreen.
            if (reason === PipCallDialogComponentCloseReason.HANG_UP || this.hangUpFromPipInProgress) {
                this.hangUpFromPipInProgress = false;
                this.isPip$.next(false);
                this.pipDialog = null;
                return;
            }

            // Fullscreen / dismiss — keep call, only leave PIP
            this.isPip$.next(false);
            this.pipDialog = null;

            // Stopping share here is for leaving PIP while still in call, not hangup
            if (this.isScreenCapture$.getValue()) {
                void this.stopScreenCapture();
            }
        });

        return new Promise(resolve => {
            this.pipDialog
                .afterOpened()
                .pipe(take(1))
                .subscribe(() => {
                    this.onPipOpened();
                    // Dialog AfterViewInit may lag afterOpened; push current mute/cam once more
                    queueMicrotask(() => this.syncPipMediaState());
                    resolve();
                });
        });
    }

    /**
     * Push current cam/mic/partner flags into an already-open PIP dialog.
     */
    private syncPipMediaState(): void {
        if (!this.pipDialog) {
            return;
        }
        this.pipDialogService.remoteVideoToggled.emit(this.hasCallPartnerVideoDisabled$.getValue());
        this.pipDialogService.remoteAudioToggled.emit(this.hasCallPartnerAudioDisabled$.getValue());
        this.pipDialogService.localVideoToggled.emit(this.cameraActive$.getValue());
        this.pipDialogService.localMicrophoneToggled.emit(this.microphoneActive$.getValue());
        this.pipDialogService.hasCallPartnerChange.emit(this.hasCallPartner$.getValue());
    }

    /**
     * Bind PIP streams and wire controls once the dialog is open.
     */
    private onPipOpened(): void {
        this.updatePipRemoteStream();
        this.updatePipLocalStream();

        this.pipSubscriptionsHolder.add(
            this.hasCallPartner$.pipe(skip(1)).subscribe(hasCallPartner => {
                this.pipDialogService.hasCallPartnerChange.emit(hasCallPartner);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.hasCallPartnerVideoDisabled$.subscribe(value => {
                this.pipDialogService.remoteVideoToggled.emit(value);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.hasCallPartnerAudioDisabled$.subscribe(value => {
                this.pipDialogService.remoteAudioToggled.emit(value);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.cameraActive$.subscribe(value => {
                this.pipDialogService.localVideoToggled.emit(value);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.microphoneActive$.subscribe(value => {
                this.pipDialogService.localMicrophoneToggled.emit(value);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.callService.getCallSession().subscribe(session => {
                this.pipDialogService.participantNamesChange.emit({
                    remote: session?.externalParticipantName || '',
                    local: session?.internalParticipantName || '',
                });
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.isScreenCapture$.subscribe(value => {
                this.pipDialogService.isScreenCaptureChange.emit(value);
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.pipDialogService.toggleCamera.subscribe(() => {
                this.toggleCamera();
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.pipDialogService.toggleMic.subscribe(() => {
                this.toggleMicrophone();
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.pipDialogService.startScreenCapture.subscribe(() => {
                this.startScreenCapture();
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.pipDialogService.stopScreenCapture.subscribe(() => {
                this.stopScreenCapture();
            }),
        );

        this.pipSubscriptionsHolder.add(
            this.pipDialogService.requestHangUp.subscribe(() => {
                this.hangUpFromPip();
            }),
        );
    }

    /**
     * Stops call
     */
    async stopCall() {
        this.sessionSubscriptionsHolder?.unsubscribe();
        this.sessionSubscriptionsHolder = new Subscription();
        this.callWsService.disconnect();
        this.stopExistingStream();
        this.stopExistingScreenCaptureStream();
        if (this.rtcPeerConnection) {
            this.rtcPeerConnection.close();
        }
        this.resetData();
        if (this.isPip$.getValue()) {
            this.pipDialogService.hungUp.emit();
        }
        this.deviceFacade.invokePostCallScript();
    }

    /**
     * Handles hang up from PIP modal.
     * Tears down share without recreate — call is ending.
     * Closes dialog with HANG_UP before stopCall so afterClosed does not look like fullscreen
     * (resetData used to close() with no reason → share stop + fullscreen flash).
     */
    hangUpFromPip() {
        if (this.hangUpFromPipInProgress) {
            return;
        }
        this.hangUpFromPipInProgress = true;
        this.isPip$.next(false);

        // Drop screen capture without createStream() (that would revive the call UI)
        this.stopExistingScreenCaptureStream();
        this.screenCaptureStream = null;
        this.isScreenCapture$.next(false);

        this.sendHangUp();
        this.callService.clearCreateCallSession();

        const dialog = this.pipDialog;
        this.pipDialog = null;
        dialog?.close(PipCallDialogComponentCloseReason.HANG_UP);

        this.stopCall();
    }

    /**
     * Notifies ws about setting call on hold
     */
    sendOnHold(): void {
        this.callWsService.send(new CallWsMessage(CallWsEvent.ON_HOLD));
    }

    /**
     * Notifies ws about hanging up
     */
    sendHangUp(): void {
        this.callWsService.send(new CallWsMessage(CallWsEvent.HANG_UP));
    }

    /**
     * Turns microphone on or off
     */
    toggleMicrophone(): void {
        const enabled = !this.microphoneActive$.getValue();

        // Set auto stream for video call
        for (const track of this.stream.getAudioTracks()) {
            track.enabled = enabled;
            // Replace the sender track
            // Needed in case of a device change like screen share toggle
            this.audioSender.replaceTrack(track);
        }

        this.microphoneActive$.next(enabled);
        this.callWsService.send(
            new CallWsMessage(CallWsEvent.TOGGLE_AUDIO, {
                is_enabled: enabled,
            }),
        );
    }

    /**
     * Tell peer current cam/mic flags (mute before join, or after renegotiate).
     */
    private publishLocalMediaState(): void {
        this.callWsService.send(
            new CallWsMessage(CallWsEvent.TOGGLE_AUDIO, {
                is_enabled: this.microphoneActive$.getValue(),
            }),
        );
        this.callWsService.send(
            new CallWsMessage(CallWsEvent.TOGGLE_VIDEO, {
                is_enabled: this.cameraActive$.getValue(),
            }),
        );
    }

    /**
     * Turns camera on or off.
     */
    async toggleCamera(): Promise<void> {
        const enabled = !this.cameraActive$.getValue();
        for (const video of this.stream.getVideoTracks()) {
            video.enabled = enabled;
        }
        this.cameraActive$.next(enabled);
        this.callWsService.send(
            new CallWsMessage(CallWsEvent.TOGGLE_VIDEO, {
                is_enabled: enabled,
            }),
        );
    }

    /**
     * Turns camera on or off
     * @param enabled boolean
     */
    async setCameraEnabled(enabled: boolean): Promise<void> {
        for (const video of this.stream.getVideoTracks()) {
            video.enabled = enabled;
        }
        this.cameraActive$.next(enabled);
        this.callWsService.send(
            new CallWsMessage(CallWsEvent.TOGGLE_VIDEO, {
                is_enabled: enabled,
            }),
        );
    }

    /**
     * Inits ws events listening
     */
    private async handleWsEvents({ event, data }: CallWsMessage) {
        switch (event) {
            // Create and send offer when both calees joined
            case CallWsEvent.ALL_CALLEES_AVAILABLE:
                // Sync session once all callees available
                this.callService.loadCurrentSession();

                if (this.rtcPeerConnection) {
                    this.rtcPeerConnection.close();
                }

                this.rtcPeerConnection = new RTCPeerConnection({
                    iceServers: this.callConfig.iceServers,
                });
                this.addLocalTracks(this.rtcPeerConnection);
                // Web left mid-share: new PC gets camera tracks — re-attach live screen track
                this.reapplyScreenShareIfActive();
                this.rtcPeerConnection.ontrack = this.setRemoteStream.bind(this);
                this.rtcPeerConnection.onicecandidate = this.sendIceCandidate.bind(this);
                await this.createOffer(this.rtcPeerConnection);
                this.publishLocalMediaState();
                break;
            // Set remote description when answer is retrieved
            case CallWsEvent.WEB_RTC_ANSWER:
                this.rtcPeerConnection.setRemoteDescription(new RTCSessionDescription(data));
                this.setRtpSenderMaxBitrate(this.audioSender, this.getWebRtcMediaConfig('audio.maxBitrate'));
                this.setRtpSenderMaxBitrate(this.videoSender, this.getWebRtcMediaConfig('video.maxBitrate'));
                break;

            // Add ice candidate when retrieved
            case CallWsEvent.SEND_ICE_CANDIDATES: {
                // ICE candidate configuration.
                const candidate = new RTCIceCandidate({
                    sdpMLineIndex: data.label,
                    candidate: data.candidate,
                });
                this.rtcPeerConnection.addIceCandidate(candidate);
                break;
            }

            case CallWsEvent.CALLEE_LEFT:
                this.remoteVideo.nativeElement.srcObject = null;
                this.remoteStream = null;
                this.hasCallPartner$.next(false);
                // Do not keep previous peer's cam/mic-off overlay for the next joiner
                this.hasCallPartnerVideoDisabled$.next(false);
                this.hasCallPartnerAudioDisabled$.next(false);
                this.updatePipRemoteStream();

                // Sync session once callee left
                this.callService.loadCurrentSession();
                break;

            // Set remote description when answer is retrieved
            case CallWsEvent.TOGGLE_VIDEO:
                this.hasCallPartnerVideoDisabled$.next(!data.is_enabled);
                break;

            case CallWsEvent.TOGGLE_AUDIO:
                this.hasCallPartnerAudioDisabled$.next(!data.is_enabled);
                break;
            default:
                break;
        }
    }

    /**
     * If screenshare was still active when the peer left, put the screen track
     * back on the new peer connection and tell the rejoining peer.
     */
    private reapplyScreenShareIfActive(): void {
        if (!this.isScreenCapture$.getValue() || !this.screenCaptureStream) {
            return;
        }

        const track = this.screenCaptureStream.getVideoTracks()[0];
        if (!track || track.readyState === 'ended') {
            this.isScreenCapture$.next(false);
            this.screenCaptureStream = null;
            return;
        }

        this.setVideoTrackToScreenShare();
        this.callWsService.send(new CallWsMessage(CallWsEvent.TOGGLE_SCREEN_SHARE, { is_enabled: true }));
    }

    /**
     * Starts screen capture of the Mona window.
     * Keeps current camera on/off — do not force the face tile on.
     * PIP local preview stays on the camera stream (not the screen track — feedback loop).
     */
    async startScreenCapture() {
        if (this.isScreenCapture$.getValue()) {
            return;
        }

        await this.createScreenCaptureStream();
        if (!this.screenCaptureStream) {
            return;
        }

        this.setVideoTrackToScreenShare();

        this.isScreenCapture$.next(true);
        if (this.isPip$.getValue()) {
            this.updatePipLocalStream();
        }

        this.callWsService.send(new CallWsMessage(CallWsEvent.TOGGLE_SCREEN_SHARE, { is_enabled: true }));
    }

    /**
     * stops Screen Capture stream.
     * Keeps whatever camera on/off the user set during share (setVideoTrackToCamera applies cameraActive$).
     */
    async stopScreenCapture() {
        await this.createStream();
        this.setVideoTrackToCamera();

        this.isScreenCapture$.next(false);
        this.callWsService.send(new CallWsMessage(CallWsEvent.TOGGLE_SCREEN_SHARE, { is_enabled: false }));
    }

    /**
     * Sets video track to camera
     */
    setVideoTrackToCamera() {
        // We have to replace both, the audio and the video. If we do only one of them, the other one does not work
        // anymore
        this.stream.getAudioTracks().forEach(track => {
            track.enabled = this.microphoneActive$.getValue();
            this.audioSender.replaceTrack(track);
        });
        this.stream.getVideoTracks().forEach(track => {
            track.enabled = this.cameraActive$.getValue();
            this.videoSender.replaceTrack(track);
        });

        if (this.localVideoElement) {
            this.localVideoElement.nativeElement.srcObject = this.stream;
        }

        // Update PIP local stream if dialog is open
        if (this.isPip$.getValue()) {
            this.updatePipLocalStream();
        }
    }

    /**
     * Sends screen capture on the peer connection video sender.
     * Local / PIP preview keeps the camera stream so the Mona window capture includes the face.
     */
    setVideoTrackToScreenShare() {
        // We have to replace both, the audio and the video. If we do only one of them, the other one does not work
        // anymore
        this.stream.getAudioTracks().forEach(track => {
            track.enabled = this.microphoneActive$.getValue();
            this.audioSender.replaceTrack(track);
        });
        this.screenCaptureStream.getVideoTracks().forEach(track => {
            this.videoSender.replaceTrack(track);
        });

        // Preview stays on camera — screen is what we send, faces must remain in the captured UI
        if (this.localVideoElement) {
            this.localVideoElement.nativeElement.srcObject = this.stream;
        }

        if (this.isPip$.getValue()) {
            this.updatePipLocalStream();
        }
    }

    /**
     * Creates stream from screen capture
     */
    async createScreenCaptureStream(): Promise<void> {
        // Stop old stream
        this.stopExistingScreenCaptureStream();

        // Declare stream
        let stream: MediaStream;

        try {
            const sourceId = await window.MonaTerminal.chrome.getMediaSourceId();

            // Grab stream
            stream = (await (navigator.mediaDevices as any).getUserMedia({
                audio: false,
                video: {
                    mandatory: {
                        chromeMediaSource: 'desktop',
                        chromeMediaSourceId: sourceId,
                        minWidth: 1280,
                        maxWidth: 1920,
                        minHeight: 720,
                        maxHeight: 1080,
                    },
                },
            })) as MediaStream;
        } catch (error) {
            this.logger.warn('Could not get screen media', error);
            this.messageService.errorToast('apps.telemedicine.cannotGetScreenCapture', {
                errorCode: MEDIA_ERRORS.CAN_NOT_GET_MEDIA,
                originalError: error,
            });
        }

        this.screenCaptureStream = stream;
    }

    /**
     * Creates stream
     */
    async createStream(): Promise<void> {
        this.stopExistingStream();
        await this.retryToShowCameraWithWait(5, 1000);
    }

    /**
     * Add retrying logic to camera enabling
     * @param maxRetries
     * @param delayBetweenRetries
     */
    async retryToShowCameraWithWait(maxRetries: number, delayBetweenRetries: number) {
        for (let attemptNumber = 1; attemptNumber <= maxRetries; attemptNumber++) {
            try {
                console.log(`Attempt ${attemptNumber}`);
                await this.showCamera();
                if (!this.stream) {
                    throw 'No device detected';
                }
                return;
            } catch (error) {
                console.error(`Attempt ${attemptNumber} failed:`, error);

                if (attemptNumber < maxRetries) {
                    console.log(`Retrying in ${delayBetweenRetries / 1000} seconds...`);
                    await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
                } else {
                    console.error('Max retries reached. Operation failed.');
                }
            }
        }
    }

    /**
     * Output camera stream on the screen
     */
    async showCamera(): Promise<void> {
        try {
            const mediaConstraints: MediaStreamConstraints = {
                audio: this.getWebRtcMediaConfig('audio.constraints'),
                video: this.getWebRtcMediaConfig('video.constraints'),
            };
            this.logger.log('Use MediaStreamConstraints', mediaConstraints);
            this.stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
            if (this.stream) {
                this.localVideoElement.nativeElement.srcObject = this.stream;
            }
        } catch (error) {
            this.stream = null;
            this.logger.warn('Could not get user media', error);
        }
    }

    /**
     * Clear device changing listener
     */
    clearCameraSubscription(): void {
        navigator.mediaDevices.ondevicechange = null;
    }

    /**
     * Stops existing stream
     */
    private stopExistingStream(): void {
        if (this.stream) {
            for (const track of this.stream.getTracks()) {
                track.stop();
            }
        }
    }

    /**
     * Stops existing stream
     */
    private stopExistingScreenCaptureStream(): void {
        if (this.screenCaptureStream) {
            for (const track of this.screenCaptureStream.getTracks()) {
                track.stop();
            }
        }
    }

    /**
     * Set RTP sender maximun bitrate
     * @param sender RTCRtpSender
     * @param maxBitrate number
     */
    private setRtpSenderMaxBitrate(sender: RTCRtpSender, maxBitrate: number): void {
        const parameters = sender.getParameters();
        parameters.encodings[0].maxBitrate = maxBitrate;
        sender
            .setParameters(parameters)
            .then(() => {
                this.logger.log(`Set ${sender.track.kind} maxBitrate = ${maxBitrate}`);
            })
            .catch(e => this.logger.error(e));
    }

    /**
     * Adds local tracks to connection
     * @param rtcPeerConnection RTCPeerConnection
     */
    private addLocalTracks(rtcPeerConnection: RTCPeerConnection): void {
        const audioContentHint = this.getWebRtcMediaConfig('audio.contentHint');
        const audioTrack = this.stream.getAudioTracks()[0];
        audioTrack['contentHint'] = audioContentHint;
        this.audioSender = rtcPeerConnection.addTrack(audioTrack, this.stream);
        this.logger.log(`Set audio contentHint = ${audioContentHint}`);

        const videoContentHint = this.getWebRtcMediaConfig('video.contentHint');
        const videoTrack = this.stream.getVideoTracks()[0];
        videoTrack['contentHint'] = videoContentHint;
        this.videoSender = rtcPeerConnection.addTrack(videoTrack, this.stream);
        this.logger.log(`Set video contentHint = ${videoContentHint}`);
    }

    /**
     * Sets remote stream
     * @param event event
     */
    private setRemoteStream(event): void {
        this.remoteVideo.nativeElement.srcObject = event.streams[0];
        this.remoteStream = event.streams[0];
        this.hasCallPartner$.next(true);
        // New peer — assume cam/mic on until they signal otherwise
        this.hasCallPartnerVideoDisabled$.next(false);
        this.hasCallPartnerAudioDisabled$.next(false);
        this.updatePipRemoteStream();
    }

    /**
     * Sends ICE candidate to ws
     * @param event event
     */
    private sendIceCandidate(event): void {
        if (event.candidate) {
            this.callWsService.send(
                new CallWsMessage(CallWsEvent.SEND_ICE_CANDIDATES, {
                    label: event.candidate.sdpMLineIndex,
                    candidate: event.candidate.candidate,
                }),
            );
        }
    }

    /**
     * Creates and sends RTC offer
     * @param rtcPeerConnection RTCPeerConnection
     */
    private async createOffer(rtcPeerConnection: RTCPeerConnection): Promise<void> {
        let sessionDescription;
        try {
            sessionDescription = await rtcPeerConnection.createOffer();
            rtcPeerConnection.setLocalDescription(sessionDescription);
        } catch (error) {
            this.logger.error(error);
        }

        this.callWsService.send(new CallWsMessage(CallWsEvent.WEB_RTC_OFFER, sessionDescription));
    }

    /**
     * Resets all properties
     */
    private resetData(): void {
        this.cameraActive$.next(true);
        this.microphoneActive$.next(true);
        this.hasCallPartner$.next(false);
        this.hasCallPartnerVideoDisabled$.next(false);
        this.hasCallPartnerAudioDisabled$.next(false);
        this.isScreenCapture$.next(false);
        this.stream = null;
        this.remoteStream = null;
        this.localVideoElement = null;
        this.remoteVideo = null;
        this.rtcPeerConnection = null;
        this.sessionSubscriptionsHolder = new Subscription();
        this.isPip$.next(false);
        if (this.pipDialog) {
            this.pipDialog.close();
        }
        this.pipDialog = null;
    }

    /**
     * Updates remote stream in pip modal
     */
    private updatePipRemoteStream() {
        this.pipDialogService.setRemoteStream.emit(this.remoteStream);
    }

    /**
     * Updates local stream in pip modal
     */
    private updatePipLocalStream() {
        this.pipDialogService.setLocalStream.emit(this.stream);
    }
    // TODO: implemented empty methods for common interface. do we need it actually?
    replaceAudioVideoTrack: (data: MediaDeviceInfo) => void;
    updateAudioOutput: (data: MediaDeviceInfo) => void;
    getMediaData: (includeAudioOutput: boolean) => null;
}
